/**
 * The message threads behind the mock Inbox.
 *
 * These are written, not generated: Hindi in Devanagari, Hinglish, and English, with the real
 * Main Character products and rupee prices, real template names, and the delivery states Meta
 * actually reports. A screen full of "John Doe" and "Lorem ipsum" teaches us nothing about
 * whether this design works, so there is none of it here.
 *
 * Author: Danish Khan
 */
import type { DeliveryState, Direction, MessageKind, OutboundReason } from '../lib/types.ts';

export interface SeedMessage {
  /** Minutes before "now". Larger = older. */
  ago: number;
  dir: Direction;
  body: string;
  kind?: MessageKind;
  delivery?: DeliveryState;
  reason?: OutboundReason;
  failure?: string;
  template?: { name: string; language: string; params: string[] };
  /** Product ids whose hero image rode along with this message. */
  media?: string[];
  /** Human reply: the rep who typed it. */
  author?: string;
  /** Bot turn id, so the Explain panel has something to open. */
  turn?: string;
}

export interface SeedThread {
  key: string;
  messages: SeedMessage[];
}

export const THREADS: SeedThread[] = [
  {
    key: 'anita',
    messages: [
      { ago: 196, dir: 'in', body: 'नमस्ते, मुझे अपने लिविंग रूम के लिए एक साइड टेबल चाहिए' },
      {
        ago: 195,
        dir: 'out',
        turn: 't-anita-1',
        reason: 'turn',
        delivery: 'read',
        body: 'नमस्ते! मैं माया हूँ, Main Character से। ज़रूर — लिविंग रूम के लिए साइड टेबल। क्या यह आपके घर के लिए है या किसी प्रोजेक्ट के लिए?',
      },
      { ago: 190, dir: 'in', body: 'घर के लिए, अपने ड्रॉइंग रूम के लिए' },
      {
        ago: 189,
        dir: 'out',
        turn: 't-anita-2',
        reason: 'turn',
        delivery: 'read',
        body: 'समझ गई। आपको कैसा लुक पसंद आता है — मॉडर्न, क्लासिक, या कुछ स्टेटमेंट पीस जैसा?',
      },
      { ago: 184, dir: 'in', body: 'कुछ statement type, marble में हो तो अच्छा' },
      {
        ago: 183,
        dir: 'out',
        turn: 't-anita-3',
        reason: 'turn',
        delivery: 'read',
        kind: 'image',
        media: ['mc-cantilever-marble', 'mc-agate-top-side-table'],
        body: 'ये दो पीस आपको पसंद आ सकते हैं। Cantilever Marble (KL 1483 A) — ₹1,10,000, पूरा marble में। और Agate Top Cocktail Table (KL 2105) — ₹32,000, agate stone top के साथ।',
      },
      { ago: 176, dir: 'in', body: 'Cantilever वाला बहुत सुंदर है 😍 इसकी height कितनी है?' },
      {
        ago: 175,
        dir: 'out',
        turn: 't-anita-4',
        reason: 'turn',
        delivery: 'read',
        body: 'Cantilever Marble की साइज़ 45L x 45W x 50H cm है। कस्टम साइज़ और फ़िनिश भी बन सकते हैं। क्या मैं हमारी डिज़ाइन टीम से आपको एक कॉल करवा दूँ, वे बाक़ी डिटेल पर बात कर लेंगे?',
      },
      { ago: 168, dir: 'in', body: 'हाँ, please कॉल करवा दीजिए। मैं अनीता शर्मा, मुंबई से।' },
      {
        ago: 167,
        dir: 'out',
        turn: 't-anita-5',
        reason: 'turn',
        delivery: 'delivered',
        body: 'शुक्रिया अनीता जी। हमारी डिज़ाइन टीम आज शाम 7 बजे से पहले आपको कॉल करेगी। तब तक कुछ और देखना चाहें तो बताइएगा।',
      },
      { ago: 41, dir: 'in', body: 'कॉल का कोई अपडेट?' },
    ],
  },
  {
    key: 'karthik',
    messages: [
      { ago: 640, dir: 'in', body: 'Hi, Athena credenza ka price kya hai?' },
      {
        ago: 639,
        dir: 'out',
        turn: 't-karthik-1',
        reason: 'turn',
        delivery: 'read',
        body: 'Hi! Main Maya, Main Character se. Athena Credenza W/M (KL 1833) ₹2,11,000 hai — engineered wood structure with metal cladding, 180L x 45W x 80H cm. Yeh ghar ke liye dekh rahe hain ya kisi project ke liye?',
      },
      { ago: 631, dir: 'in', body: 'Ghar ke liye. Dining room mein rakhna hai' },
      {
        ago: 630,
        dir: 'out',
        turn: 't-karthik-2',
        reason: 'turn',
        delivery: 'read',
        kind: 'image',
        media: ['mc-athena-credenza-w-m', 'mc-chequered-credenza'],
        body: 'Dining ke liye Athena bahut achha kaam karta hai. Ek aur option — Chequered Credenza (KL 2096), ₹1,35,000, brass sheet cladding ke saath marble top. Dono ki photos bheji hain.',
      },
      { ago: 622, dir: 'in', body: 'Chequered zyada practical lag raha. Delivery Chennai ho jayegi?' },
      {
        ago: 621,
        dir: 'out',
        turn: 't-karthik-3',
        reason: 'turn',
        delivery: 'read',
        body: 'Ji haan, hum pan-India ship karte hain. Dispatch 8 weeks mein hota hai aur shipping checkout par calculate hoti hai. Aapka budget range roughly kya soch rahe hain?',
      },
      { ago: 615, dir: 'in', body: '1.5 lakh tak theek hai' },
      {
        ago: 614,
        dir: 'out',
        turn: 't-karthik-4',
        reason: 'turn',
        delivery: 'read',
        body: 'Perfect — Chequered Credenza us range mein hai. Kya aap studio visit karna chahenge ya call par baat kar lein?',
      },
      { ago: 90, dir: 'in', body: 'Call theek rahega, par is week nahi. Agle Monday?' },
    ],
  },
  {
    key: 'rohit',
    messages: [
      {
        ago: 1450,
        dir: 'in',
        body: "Hello — we're fitting out a 40-key boutique hotel in Jaipur, opening March. Looking for console tables and mirrors for the rooms and lobby.",
      },
      {
        ago: 1449,
        dir: 'out',
        turn: 't-rohit-1',
        reason: 'turn',
        delivery: 'read',
        body: "Hello! I'm Maya from Main Character. A 40-key property in Jaipur — that's a lovely brief. Are you looking at one repeated piece per room plus statement pieces for the lobby, or a mix?",
      },
      { ago: 1440, dir: 'in', body: 'One console + one mirror per room, repeated. Lobby is separate, we want something large.' },
      {
        ago: 1439,
        dir: 'out',
        turn: 't-rohit-2',
        reason: 'turn',
        delivery: 'read',
        kind: 'image',
        media: ['mc-arch-console-1', 'mc-bombay-mirror-frame'],
        body: 'For a repeated room set, Arch Console (KL 2194) at ₹65,000 and Bombay Mirror Frame (KL 1280) at ₹38,000 pair well and both take custom finishes. For 40 rooms our team can quote project pricing rather than list.',
      },
      { ago: 1430, dir: 'in', body: 'What sort of lead time on 80 pieces?' },
      {
        ago: 1429,
        dir: 'out',
        turn: 't-rohit-3',
        reason: 'turn',
        delivery: 'read',
        body: 'For a volume of that size the lead time is set per project rather than the standard 8 weeks — our design team will confirm it against your March opening. Shall I have them call you today?',
      },
      { ago: 1420, dir: 'in', body: 'Yes please. Rohit Bansal, Bansal Hospitality. Best on this number after 4pm.' },
      {
        ago: 1419,
        dir: 'out',
        turn: 't-rohit-4',
        reason: 'turn',
        delivery: 'read',
        body: 'Thank you Rohit. I have passed this to our design team with your March timeline and the 80-piece volume. They will call you after 4pm.',
      },
      {
        ago: 1180,
        dir: 'out',
        reason: 'human',
        author: 'Ritu Bhandari',
        delivery: 'read',
        body: 'Hi Rohit, Ritu here from the Main Character design team. Tried you at 4.20pm — will try again tomorrow morning. Meanwhile I am putting together a project sheet for the 40-key set.',
      },
      { ago: 1120, dir: 'in', body: 'Sorry, was in a site meeting. Tomorrow 11am works.' },
      {
        ago: 300,
        dir: 'out',
        reason: 'human',
        author: 'Ritu Bhandari',
        delivery: 'read',
        body: 'Spoke this morning — sending the project sheet with the Arch Console and Bombay Mirror in the antique brass finish, plus two lobby options.',
      },
      { ago: 62, dir: 'in', body: 'Received, thank you. The lobby console at 120k — is that with the marble top?' },
    ],
  },
  {
    key: 'farah',
    messages: [
      { ago: 520, dir: 'in', body: 'The Casablanca mirror arrived yesterday and the bottom right corner is chipped. Quite disappointed.' },
      {
        ago: 519,
        dir: 'out',
        turn: 't-farah-1',
        reason: 'turn',
        delivery: 'read',
        body: 'I am sorry, that is not how the piece should reach you. I am putting you through to our team right away so they can sort the replacement — they will come back to you on this number.',
      },
      {
        ago: 505,
        dir: 'out',
        reason: 'human',
        author: 'Sneha Pillai',
        delivery: 'read',
        body: 'Hello Farah, this is Sneha from Main Character. I am very sorry about the chip. Could you send a photo of the corner? I will raise the replacement today — you will not need to return the piece first.',
      },
      { ago: 498, dir: 'in', kind: 'image', body: '[photo of chipped corner]' },
      {
        ago: 494,
        dir: 'out',
        reason: 'human',
        author: 'Sneha Pillai',
        delivery: 'read',
        body: 'Thank you. Replacement raised, reference KL250-RPL-0912. Dispatch in 10 days and we will collect the damaged piece at the same time.',
      },
      { ago: 120, dir: 'in', body: 'Appreciated. Please confirm the collection date when you have it.' },
    ],
  },
  {
    key: 'neha',
    messages: [
      { ago: 4400, dir: 'in', body: 'क्या आपके पास bone inlay के bedside tables हैं?' },
      {
        ago: 4399,
        dir: 'out',
        turn: 't-neha-1',
        reason: 'turn',
        delivery: 'read',
        kind: 'image',
        media: ['mc-bone-inlay-2-drawers-bedside', 'mc-diamond-bed-side-bone-inlay'],
        body: 'जी हाँ। Bone Inlay 2 Drawers Bedside (KL 261) — ₹40,000, और Diamond Bed Side Bone Inlay (KL 1910) — ₹39,000। दोनों कस्टम कलर में बन सकते हैं।',
      },
      { ago: 4390, dir: 'in', body: 'सोचकर बताती हूँ' },
      {
        ago: 4389,
        dir: 'out',
        turn: 't-neha-2',
        reason: 'turn',
        delivery: 'read',
        body: 'ज़रूर, कोई जल्दी नहीं। कुछ और देखना हो तो बता दीजिएगा।',
      },
      {
        ago: 1500,
        dir: 'out',
        kind: 'template',
        reason: 'template',
        delivery: 'delivered',
        // `language: ''`, as the real API answers it: `convo.messages` stores the template name
        // and its params but has no language column, so `routes/inbox.ts` sends an empty string.
        // The mock used to fill it in, which hid the bubble's dangling separator from review.
        template: { name: 'reengage_bedside_hi', language: '', params: ['नेहा', 'Bone Inlay 2 Drawers Bedside'] },
        body: 'नमस्ते नेहा जी, पिछली बार आपने Bone Inlay 2 Drawers Bedside देखा था। वह अभी उपलब्ध है — देखना चाहेंगी?',
      },
    ],
  },
  {
    key: 'ayesha',
    messages: [
      { ago: 55, dir: 'in', body: 'Hi! Saw your Instagram. Do you make console tables in a smaller size? My hallway is narrow.' },
      {
        ago: 54,
        dir: 'out',
        turn: 't-ayesha-1',
        reason: 'turn',
        delivery: 'delivered',
        body: 'Hi! Maya here from Main Character. Yes — most of our consoles can be made to a custom width. Roughly how wide is the hallway wall you are working with?',
      },
      { ago: 22, dir: 'in', body: 'About 3 feet only' },
    ],
  },
  {
    key: 'mehul',
    messages: [
      { ago: 310, dir: 'in', body: 'Do you have anything in agate or onyx?' },
      {
        ago: 309,
        dir: 'out',
        turn: 't-mehul-1',
        reason: 'turn',
        delivery: 'read',
        kind: 'image',
        media: ['mc-agate-top-side-table'],
        body: 'We do — Agate Top Cocktail Table (KL 2105), ₹32,000, agate stone top on a marble base. Is this for a living room or a bedroom?',
      },
      { ago: 300, dir: 'in', body: 'Living room. Bhaav thoda kam ho sakta hai kya?' },
      {
        ago: 299,
        dir: 'out',
        turn: 't-mehul-2',
        reason: 'turn',
        delivery: 'read',
        body: 'Listed price ₹32,000 hai — discount par main commit nahi kar sakti, lekin hamari team aapse baat kar sakti hai. Kya main unhe aapka number de doon?',
      },
      { ago: 292, dir: 'in', body: 'Abhi nahi, pehle wife ko dikhata hoon' },
    ],
  },
  {
    key: 'priyanka',
    messages: [
      { ago: 1010, dir: 'in', body: 'प्रियंका शर्मा. मुझे mirror चाहिए, entryway के लिए' },
      {
        ago: 1009,
        dir: 'out',
        turn: 't-priyanka-1',
        reason: 'turn',
        delivery: 'read',
        kind: 'image',
        media: ['mc-arabesque-mirror-frame', 'mc-chanel-mirror-frame'],
        body: 'नमस्ते प्रियंका जी। Entryway के लिए दो सुंदर विकल्प — Arabesque Mirror Frame (KL 1155) ₹55,000, brass sheet cladding में, और Chanel Mirror Frame (KL 1387) ₹45,000, bone inlay में।',
      },
      { ago: 1000, dir: 'in', body: 'Arabesque पसंद आया। कितना बड़ा है?' },
      {
        ago: 999,
        dir: 'out',
        turn: 't-priyanka-2',
        reason: 'turn',
        delivery: 'read',
        body: 'Arabesque Mirror Frame 90L x 4W x 180H cm है। कस्टम साइज़ भी बन सकता है। आपकी entryway की दीवार कितनी ऊँची है?',
      },
      { ago: 640, dir: 'in', body: 'मैं नाप कर बताती हूँ' },
      {
        ago: 300,
        dir: 'out',
        reason: 'human',
        author: 'R. Karthik',
        delivery: 'failed',
        failure: 'Message failed: 24-hour customer service window closed (131047). Use an approved template.',
        body: 'प्रियंका जी, नाप मिल गया हो तो बता दीजिए — मैं Arabesque का custom quote निकाल दूँ।',
      },
    ],
  },
  {
    key: 'siddharth',
    messages: [
      { ago: 88, dir: 'in', body: 'what is the price of the arch console' },
      {
        ago: 87,
        dir: 'out',
        turn: 't-siddharth-1',
        reason: 'turn',
        delivery: 'delivered',
        body: 'Arch Console comes in two sizes — KL 2062 at ₹1,20,000 and KL 2194 at ₹65,000, both in brass sheet cladding over a wood structure. Which room are you furnishing?',
      },
      { ago: 80, dir: 'in', body: 'the cheaper one. can someone call me today' },
    ],
  },
  {
    key: 'tanvi',
    messages: [
      { ago: 2900, dir: 'in', body: 'Hi, do you ship to Pune?' },
      {
        ago: 2899,
        dir: 'out',
        turn: 't-tanvi-1',
        reason: 'turn',
        delivery: 'read',
        body: 'Hi! Yes, we ship across India including Pune. Dispatch is within 8 weeks and shipping is calculated at checkout. What are you looking for?',
      },
      { ago: 2880, dir: 'in', body: 'A coffee table, something in wood and brass' },
      {
        ago: 2879,
        dir: 'out',
        turn: 't-tanvi-2',
        reason: 'turn',
        delivery: 'read',
        kind: 'image',
        media: ['mc-cross-leg-coffee-table'],
        body: 'Cross Leg Coffee Table (KL1953) — ₹73,000, engineered wood with bone inlay and powder coated MS legs. Would you like to see it in a different finish?',
      },
      { ago: 2400, dir: 'in', body: 'Nice. Will discuss at home and revert.' },
    ],
  },
  {
    key: 'srinivasan',
    messages: [
      { ago: 5800, dir: 'in', body: 'K. Srinivasan here. Need 6 identical side tables for a serviced apartment block.' },
      {
        ago: 5799,
        dir: 'out',
        turn: 't-srini-1',
        reason: 'turn',
        delivery: 'read',
        body: 'Thank you for reaching out. Six identical pieces is a project order — our design team handles pricing on those. Could you tell me the style you have in mind and the city?',
      },
      { ago: 5780, dir: 'in', body: 'Bengaluru. Simple, marble top preferred.' },
      {
        ago: 5779,
        dir: 'out',
        turn: 't-srini-2',
        reason: 'turn',
        delivery: 'read',
        kind: 'image',
        media: ['mc-brass-cube-with-marble-top'],
        body: 'Brass Cube With Marble Top (KL 1310) at ₹29,000 is the cleanest of our marble-top pieces and repeats well across units. I have passed your requirement to our design team for project pricing.',
      },
      {
        ago: 5400,
        dir: 'out',
        reason: 'human',
        author: 'Ritu Bhandari',
        delivery: 'read',
        body: 'Mr Srinivasan, Ritu from Main Character. For 6 units of KL 1310 we can do a project price — may I call you tomorrow at 11am to confirm the finish?',
      },
      { ago: 5340, dir: 'in', body: 'Tomorrow is fine.' },
    ],
  },
  {
    key: 'sumitra',
    messages: [
      { ago: 760, dir: 'in', body: 'भाव बताइए' },
      {
        ago: 759,
        dir: 'out',
        turn: 't-sumitra-1',
        reason: 'turn',
        delivery: 'read',
        body: 'नमस्ते! ज़रूर बताऊँगी — किस पीस का भाव चाहिए? या बता दीजिए कि कौन सा कमरा सजा रही हैं, मैं कुछ विकल्प भेज देती हूँ।',
      },
      { ago: 700, dir: 'in', body: 'बैठक के लिए एक टेबल' },
      {
        ago: 699,
        dir: 'out',
        turn: 't-sumitra-2',
        reason: 'turn',
        delivery: 'read',
        kind: 'image',
        media: ['mc-damru-side-table', 'mc-bone-inlay-side-table'],
        body: 'बैठक के लिए — Damru Side Table (KL 1329) ₹35,000 और Bone Inlay Side Table (KL 1335) ₹28,000। दोनों bone inlay के काम में हैं।',
      },
      { ago: 690, dir: 'in', body: '28 हज़ार वाला ठीक है' },
    ],
  },
  {
    key: 'zoya',
    messages: [
      { ago: 175, dir: 'in', body: 'Hey! Do you do bar cabinets?' },
      {
        ago: 174,
        dir: 'out',
        turn: 't-zoya-1',
        reason: 'turn',
        delivery: 'read',
        body: 'Hi! We do cabinets and credenzas — Aziza 2 Door Cabinet (KL 1723) at ₹93,000 and Aurora Round Cabinet (KL 1831) at ₹1,50,000 are the two that people use as bar pieces. Shall I send photos?',
      },
      { ago: 168, dir: 'in', body: 'yes please' },
      {
        ago: 167,
        dir: 'out',
        turn: 't-zoya-2',
        reason: 'turn',
        delivery: 'read',
        kind: 'image',
        media: ['mc-aziza-2-door-cabinet', 'mc-aurora-round-cabinet'],
        body: 'Here they are. Aziza is bone inlay on a brass plated stand; Aurora is bone inlay with a powder coated base. Both can be fitted out inside for bottles and glassware on request.',
      },
      { ago: 150, dir: 'in', body: 'Aziza. What colours?' },
      {
        ago: 149,
        dir: 'out',
        turn: 't-zoya-3',
        reason: 'turn',
        delivery: 'delivered',
        body: 'Aziza comes in black, white and brass as standard, and custom colours on request. Would you like our design team to talk you through the interior fit-out?',
      },
    ],
  },
  {
    key: 'vikram',
    messages: [
      { ago: 2100, dir: 'in', body: 'Restaurant project, 12 tables + entrance console. Timeline is tight — opening in 6 weeks.' },
      {
        ago: 2099,
        dir: 'out',
        turn: 't-vikram-1',
        reason: 'turn',
        delivery: 'read',
        body: 'Thank you — a 6-week opening with 13 pieces is tight against our standard 8-week dispatch, so I am passing this to our design team now rather than guessing at a date. They will call you today.',
      },
      {
        ago: 2050,
        dir: 'out',
        reason: 'human',
        author: 'Ritu Bhandari',
        delivery: 'read',
        body: 'Mr Chauhan, Ritu from Main Character. On 6 weeks we can commit to the entrance console and 6 of the 12 tables from ready stock; the rest would land 3 weeks later. Would a phased delivery work?',
      },
      { ago: 1990, dir: 'in', body: 'Phased is not ideal but workable. Send the split in writing.' },
      {
        ago: 1900,
        dir: 'out',
        reason: 'human',
        author: 'Ritu Bhandari',
        delivery: 'read',
        body: 'Sent to your email just now — phase 1 on 24 Oct, phase 2 on 14 Nov, both ex-works Jaipur.',
      },
      { ago: 1400, dir: 'in', body: 'Received. Going with it. Send the invoice.' },
    ],
  },
];
